import { BaseComponent, html, nothing } from '../../lib/lit.js';
import { pollBadge } from '../../lib/polls.js';
import { router } from '../../lib/router.js';
import { formatItemDate } from './community-item-list.js';

/**
 * A poll's close time, to the minute: "closes Oct 10" is not enough to know
 * whether there is still time tonight.
 * @param {string} iso
 */
export function formatPollTime(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    return Number.isNaN(date.getTime())
        ? ''
        : date.toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
          });
}

/**
 * <community-polls> — the polls a member can answer, open ones first, then
 * closed ones. Each links to its page.
 */
export class CommunityPolls extends BaseComponent {
    static properties = {
        polls: { type: Array },
    };

    constructor() {
        super();
        /** @type {Array<object>} */
        this.polls = [];
    }

    _when(poll) {
        if (poll.state === 'closed') return `Closed ${formatItemDate(poll.closedAt)}`;
        if (poll.closesAt) return `Closes ${formatPollTime(poll.closesAt)}`;
        return `Opened ${formatItemDate(poll.openedAt)}`;
    }

    _renderPoll(poll) {
        const badge = pollBadge(poll);
        return html`
            <button
                type="button"
                class="list-group-item list-group-item-action text-start"
                @click=${() => router.navigate(`/polls/${poll.id}`)}
            >
                <div class="d-flex align-items-start">
                    <i class="bi bi-bar-chart me-2 mt-1 text-secondary"></i>
                    <div class="flex-grow-1 min-w-0">
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                            <span class="fw-semibold">${poll.title}</span>
                            <span class="badge ${badge.badge}">${badge.label}</span>
                        </div>
                        <div class="text-muted small">
                            ${this._when(poll)}
                            ${poll.resultsAvailable ? html` · Results available` : nothing}
                        </div>
                    </div>
                </div>
            </button>
        `;
    }

    render() {
        if (this.polls.length === 0) {
            return html`<p class="text-muted p-3 mb-0">There are no polls yet.</p>`;
        }
        return html`
            <div class="list-group list-group-flush">
                ${this.polls.map((poll) => this._renderPoll(poll))}
            </div>
        `;
    }
}

customElements.define('community-polls', CommunityPolls);
